"use client";

import FAQ from "../FAQ/FAQ";
import RoundedButton from "../roundedButton/roundedButton";
import styles from "./faqWithCta.module.scss";
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useRouter } from "next/router";

// Register GSAP plugins at the top level
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

interface FaqWithCtaProps {
  title: string;
  description: string;
  buttonTitle: string;
  buttonUrl: string;
  target: string;
}
export default function FaqWithCta({
  title,
  description,
  buttonTitle,
  buttonUrl,
  target = "",
}: FaqWithCtaProps) {
  const faqRef = useRef(null);
  const ctaRef = useRef(null);
  const scrollTriggersRef = useRef<ScrollTrigger[]>([]);
  const router = useRouter();

  useEffect(() => {
    try {
      // Kill any existing ScrollTrigger instances
      scrollTriggersRef.current.forEach((trigger) => {
        if (trigger) trigger.kill();
      });
      scrollTriggersRef.current = [];

      const faqElement = faqRef.current;
      const ctaElement = ctaRef.current;

      if (!faqElement || !ctaElement) {
        console.warn("Required elements not found for animations");
        return;
      }

      const faqTrigger = ScrollTrigger.create({
        trigger: faqElement,
        start: "top 80%",
        onEnter: () => {
          gsap.fromTo(
            faqElement,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 1,
            }
          );
        },
      });

      const ctaTrigger = ScrollTrigger.create({
        trigger: ctaElement,
        start: "top 80%",
        onEnter: () => {
          gsap.fromTo(
            ctaElement,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 1,
            }
          );
        },
      });

      scrollTriggersRef.current.push(faqTrigger, ctaTrigger);
    } catch (error) {
      console.error("Error setting up FAQ/CTA animations:", error);
    }

    return () => {
      // Cleanup on unmount
      scrollTriggersRef.current.forEach((trigger) => {
        if (trigger) trigger.kill();
      });
      scrollTriggersRef.current = [];
      gsap.killTweensOf([faqRef.current, ctaRef.current]);
    };
  }, [title, description, buttonTitle, buttonUrl]);

  return (
    <div className={styles.faqWithCta}>
      <div className={styles.faqWithCta__container}>
        <div className={styles.faqWithCta__faqWrapper}>
          <div className={styles.faqWithCta__faqWrapper__faq} ref={faqRef}>
            <FAQ />
          </div>
        </div>
        <div className={styles.faqWithCta__ctaWrapper}>
          <div className={styles.faqWithCta__ctaWrapper__cta} ref={ctaRef}>
            <h3
              className={`${
                styles.faqWithCta__ctaWrapper__cta__title
              } ${`font-size-48`}`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <div
              className={`font-size-24`}
              dangerouslySetInnerHTML={{ __html: description }}
            />
            <div className={styles.faqWithCta__ctaWrapper__cta__button}>
              <RoundedButton
                title={buttonTitle}
                url={buttonUrl}
                light={false}
                fill={true}
                target={target}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
